'use client';

import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';

// Programmatic setHash uses history.pushState, which does NOT fire a native
// 'hashchange'. We dispatch this custom event instead so the hash store updates
// without bumping `version` (version must only count real browser navigation).
const HASH_UPDATE_EVENT = 'usehash:update';

/**
 * Custom hook for reading and writing URL hash.
 * Handles browser back/forward navigation via hashchange event.
 *
 * @returns { hash, setHash, isReady, version }
 * - hash: Current hash value (without #)
 * - setHash: Function to update hash
 * - isReady: True after initial hash has been read (client-side)
 * - version: Increments on every hashchange event (even for same hash).
 *   Use in useEffect deps to re-trigger effects on repeated navigation.
 *
 * @example
 * const { hash, setHash, isReady } = useHash();
 * // Wait for isReady before using hash for initial setup
 * if (isReady && hash.startsWith('marker-')) { ... }
 */

// Helper to read hash (works only client-side)
const getHash = () =>
  typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';

interface UseHashReturn {
  hash: string;
  setHash: (newHash: string) => void;
  isReady: boolean;
  version: number;
}

export function useHash(): UseHashReturn {
  // hash + isReady come from the URL (an external store). useSyncExternalStore
  // reads the live value on the client and falls back to '' / false during SSR
  // — no setState-in-effect, no hydration mismatch. subscribe listens to both
  // real browser navigation and our programmatic update event.
  const subscribe = useCallback((callback: () => void) => {
    window.addEventListener('hashchange', callback);
    window.addEventListener(HASH_UPDATE_EVENT, callback);
    return () => {
      window.removeEventListener('hashchange', callback);
      window.removeEventListener(HASH_UPDATE_EVENT, callback);
    };
  }, []);

  const hash = useSyncExternalStore(subscribe, getHash, () => '');
  const isReady = useSyncExternalStore(subscribe, () => true, () => false);

  // version bumps on every real browser navigation (back/forward), even for the
  // same hash, so consumers can re-trigger effects on repeated navigation.
  // Set inside an event handler (not synchronously in the effect body), so this
  // is not a set-state-in-effect violation.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => { setVersion((v) => v + 1); };
    window.addEventListener('hashchange', bump);
    return () => { window.removeEventListener('hashchange', bump); };
  }, []);

  // Update hash in URL
  const setHash = useCallback((newHash: string) => {
    const hashValue = newHash.replace(/^#/, ''); // Remove # if provided

    // Avoid redundant history push if hash is already the same
    if (hashValue === getHash()) {
      return;
    }

    if (hashValue) {
      window.history.pushState(null, '', `#${hashValue}`);
    } else {
      // Remove hash from URL
      window.history.pushState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }

    // pushState doesn't fire 'hashchange'; notify the store without bumping version
    window.dispatchEvent(new Event(HASH_UPDATE_EVENT));
  }, []);

  return { hash, setHash, isReady, version };
}
